// src/pages/Esiintymiset/Esiintymiset.jsx
import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { FaArrowRight } from "react-icons/fa";
import data from "../../data/events.json";
import esiintymisetImage from "../../assets/esiintymiset_image.webp";

export default function Esiintymiset() {
  useEffect(() => {
    AOS.init({});
  }, []);

  const tulevat = data.keikat.tulevat2025;
  const menneet = Object.entries(data.keikat.menneet || {}).sort(
    ([a], [b]) => Number(b) - Number(a)
  );

  return (
    <>
      <section
        id="esiintymiset"
        aria-labelledby="esiintymiset-heading"
        className="w-full bg-joyrideLight dark:bg-joyrideNavy py-12"
      >
        <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-16 space-y-8">
          <div
            className="flex items-center justify-end space-x-4"
            data-aos="fade-down"
          >
            <h2 className="text-4xl font-lobster font-bold text-joyridePurple dark:text-joyrideCyan dark:text-shadow-lg">
              Esiintymiset
            </h2>
            <img
              src={esiintymisetImage}
              alt="Bilebändi Joyride esiintymiset ja keikkakalenteri"
              className="w-24 h-24 rounded-full object-cover"
              loading="lazy"
            />
          </div>

          <div
            className="prose max-w-none prose-lg dark:prose-invert text-joyrideNavy dark:text-joyrideLight leading-relaxed"
            data-aos="fade-up"
          >
            <p className="font-light">
              Joyride on kiertänyt Suomea keikkaillen lukuisissa eri
              tapahtumissa ja juhlatilaisuuksissa. Alta näet tulevat ja menneet
              esiintymispaikat, jotka sisältävät kuvia keikkakaupungeista
              tunnelman virittämiseksi. Mikäli haluat buukata Joyriden, voit
              tehdä sen helposti Spiderlegs Productionsin kautta ja saada
              tarjouksen sähköpostiisi.
            </p>
          </div>

          <div className="flex justify-center" data-aos="fade-up">
            <a
              href="https://spiderlegsproductions.com/esiintyja/bilebandijoyride/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 py-2 rounded-full bg-joyridePurple dark:bg-joyrideSky text-joyrideLight dark:text-joyrideNavy hover:bg-joyrideNavy font-medium dark:hover:bg-joyrideCyan hover:text-joyrideCyan transition transform hover:scale-105"
            >
              Buukkaa Joyride – tarjous suoraan sähköpostiisi!
            </a>
          </div>

          {/* Tulevat keikat */}
          <div data-aos="fade-up">
            <h3 className="text-3xl font-lobster font-semibold text-joyridePurple dark:text-joyrideCyan dark:text-shadow-sm mb-4">
              Tulevat esiintymiset 2025
            </h3>
            <ul className="space-y-4">
              {tulevat.map(
                ({ date, venue, link, backgroundImage, backgroundPosition }, i) => (
                  <li
                    key={i}
                    className="relative overflow-hidden rounded-xl shadow-lg group cursor-pointer transition-transform transform hover:scale-105 hover:bg-joyrideSky dark:hover:bg-joyrideBars"
                    style={{ height: "120px" }}
                    data-aos="fade-up"
                  >
                    <div
                      className="absolute inset-0 filter transition-all duration-700 group-hover:scale-110 group-hover:brightness-200"
                      style={{
                        backgroundImage: `url(${backgroundImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: backgroundPosition || "center",
                        backgroundBlendMode: "overlay",
                        backgroundColor: "rgba(0, 0, 0, 0.6)"
                      }}
                    />
                    <div className="relative z-10 flex flex-col md:flex-row md:justify-between h-full items-center p-6 text-center md:text-left">
                      <time
                        dateTime={date}
                        className="text-sm bg-black bg-opacity-50 text-joyrideLight px-3 py-1 rounded-md"
                      >
                        {new Date(date).toLocaleDateString("fi-FI", {
                          day: "2-digit",
                          month: "long"
                        })}{" "}
                        – {venue}
                      </time>
                      {link && (
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 md:mt-0 inline-flex items-center bg-black bg-opacity-50 text-joyrideLight px-3 py-1 rounded-md hover:underline hover:text-joyrideCyan transition"
                        >
                          Lisätietoja <FaArrowRight className="ml-2" />
                        </a>
                      )}
                    </div>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Menneet keikat */}
          <div data-aos="fade-up">
            <h3 className="text-3xl font-lobster font-semibold text-joyridePurple dark:text-joyrideCyan mb-4 dark:text-shadow-sm">
              Menneet esiintymiset
            </h3>
            {menneet.map(([year, events]) => (
              <div key={year} className="mb-6">
                <h4 className="text-2xl font-semibold mb-2 font-lobster text-joyridePurple dark:text-joyrideLight">
                  {year}
                </h4>
                <ul className="space-y-4">
                  {events.map(
                    (
                      { date, venue, backgroundImage, backgroundPosition },
                      j
                    ) => (
                      <li
                        key={j}
                        className="relative overflow-hidden rounded-xl shadow-lg group cursor-pointer transition-transform transform hover:scale-105 hover:bg-joyrideSky dark:hover:bg-joyrideBars"
                        style={{ height: "120px" }}
                        data-aos="fade-up"
                      >
                        <div
                          className="absolute inset-0 filter transition-all duration-700 group-hover:scale-110 group-hover:brightness-200"
                          style={{
                            backgroundImage: `url(${backgroundImage})`,
                            backgroundSize: "cover",
                            backgroundPosition: backgroundPosition || "center",
                            backgroundBlendMode: "overlay",
                            backgroundColor: "rgba(0, 0, 0, 0.6)"
                          }}
                        />
                        <div className="relative z-10 flex flex-col md:flex-row md:justify-between h-full items-center p-6 text-center md:text-left">
                          <time
                            dateTime={date}
                            className="text-sm bg-black bg-opacity-50 text-joyrideLight px-3 py-1 rounded-md"
                          >
                            {new Date(date).toLocaleDateString("fi-FI", {
                              day: "2-digit",
                              month: "long"
                            })}{" "}
                            – {venue}
                          </time>
                        </div>
                      </li>
                    )
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
